import {
  FilterOutlined,
  FireOutlined,
  InboxOutlined,
  SafetyOutlined,
  UndoOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Empty,
  Flex,
  Input,
  Row,
  Select,
  Skeleton,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import type { ColumnType, ColumnsType } from "antd/es/table";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ComplianceDashboardHeader from "../../components/Compliance/ComplianceDashboardHeader.component";
import ComplianceEmptyState from "../../components/Compliance/ComplianceEmptyState.component";
import ComplianceTabs from "../../components/Compliance/ComplianceTabs.component";
import UppercaseLabel from "../../components/Compliance/UppercaseLabel.component";
import {
  ComplianceBanner,
  SEVERITY_META,
  SEVERITY_ORDER,
  SLA_DAYS,
  STATUS_META,
  type Severity,
  SeverityTag,
  type Status,
  StatusTag,
  TONE,
  ageDays,
  ageLabel,
} from "../../components/Compliance/findings.shared";
import { useAppSelector } from "../../hooks/redux-hooks";
import {
  type FindingRow,
  useGetComplianceAssigneesQuery,
  useGetComplianceFindingsQuery,
  useUpdateComplianceFindingMutation,
} from "../../service/compliance.service";
import ComplianceLandingPage from "./ComplianceLandingPage.component";
import styles from "./compliance.module.scss";

// ──────────────────────────────────────────────────────────────────────────────
// Stats strip
// ──────────────────────────────────────────────────────────────────────────────

const StatTile = ({
  label,
  value,
  sublabel,
  emphasis,
  onClick,
  active,
}: {
  label: string;
  value: number | string;
  sublabel?: string;
  emphasis?: "critical" | "default";
  onClick?: () => void;
  active?: boolean;
}) => (
  <Card
    onClick={onClick}
    style={{
      background: active ? "#eff6ff" : "#fff",
      borderColor: active ? "#4f46e5" : "#e5e7eb",
      cursor: onClick ? "pointer" : "default",
      height: "100%",
    }}
    bodyStyle={{ padding: "14px 16px" }}
  >
    <Flex vertical gap={2}>
      <UppercaseLabel size="md">{label}</UppercaseLabel>
      <Typography.Title level={3} style={{ margin: 0, color: emphasis === "critical" ? TONE.danger.fg : "#0b1020" }}>
        {value}
      </Typography.Title>
      {sublabel ? <Typography.Text style={{ fontSize: 11, color: "#6b7280" }}>{sublabel}</Typography.Text> : null}
    </Flex>
  </Card>
);

const CLOSED_STATUSES: Status[] = ["closed", "false_positive"];

// ──────────────────────────────────────────────────────────────────────────────
// Page
// ──────────────────────────────────────────────────────────────────────────────

const FindingsInbox = () => {
  const navigate = useNavigate();
  const { userInfo } = useAppSelector((state) => state.auth);

  const isAdmin = !!userInfo?.show_compliance_dashboard && !!userInfo?.is_group_admin;
  const { data, isLoading } = useGetComplianceFindingsQuery(undefined, { skip: !isAdmin });
  const { data: assigneesData } = useGetComplianceAssigneesQuery(undefined, { skip: !isAdmin });
  const [updateFinding] = useUpdateComplianceFindingMutation();

  const findings = useMemo(() => data?.findings ?? [], [data]);
  const assignees = assigneesData?.assignees ?? [];

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<Status[]>(["open", "in_review"]);
  const [severityFilter, setSeverityFilter] = useState<Severity[]>([]);
  const [assigneeFilter, setAssigneeFilter] = useState<number | null>(null);
  const [quickPreset, setQuickPreset] = useState<"all" | "my" | "critical" | "unassigned" | "sla" | "archived">("all");

  // Apply preset. Archive is orthogonal to status: by default hide archived
  // from every view; the "archived" preset is the only place archived rows show.
  const presetFiltered = useMemo(() => {
    return findings.filter((f) => {
      if (quickPreset === "archived") return f.is_archived;
      if (f.is_archived) return false;
      if (quickPreset === "my") return f.assigned_to_me;
      if (quickPreset === "critical") return f.severity === "critical" && !CLOSED_STATUSES.includes(f.status);
      if (quickPreset === "unassigned") return !f.assignee_id && !CLOSED_STATUSES.includes(f.status);
      if (quickPreset === "sla") {
        const age = ageDays(f.created_at);
        return age > SLA_DAYS[f.severity] && !CLOSED_STATUSES.includes(f.status);
      }
      return true;
    });
  }, [findings, quickPreset]);

  const filtered = useMemo(() => {
    return presetFiltered.filter((f) => {
      if (statusFilter.length > 0 && !statusFilter.includes(f.status)) return false;
      if (severityFilter.length > 0 && !severityFilter.includes(f.severity)) return false;
      if (assigneeFilter !== null && f.assignee_id !== assigneeFilter) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const haystack =
          `${f.rule_name} ${f.clinician_name} ${f.client_initials} ${f.note_title} ${f.rationale} ${f.assignee_name ?? ""}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [presetFiltered, statusFilter, severityFilter, assigneeFilter, search]);

  const stats = useMemo(() => {
    const active = findings.filter((f) => !f.is_archived);
    const open = active.filter((f) => f.status === "open").length;
    const inReview = active.filter((f) => f.status === "in_review").length;
    const criticalOpen = active.filter((f) => f.severity === "critical" && !CLOSED_STATUSES.includes(f.status)).length;
    const unassigned = active.filter((f) => !f.assignee_id && !CLOSED_STATUSES.includes(f.status)).length;
    const slaBreached = active.filter((f) => {
      const age = ageDays(f.created_at);
      return age > SLA_DAYS[f.severity] && !CLOSED_STATUSES.includes(f.status);
    }).length;
    const archivedCount = findings.filter((f) => f.is_archived).length;
    return { open, inReview, criticalOpen, unassigned, slaBreached, archivedCount };
  }, [findings]);

  // Users whose group isn't provisioned for compliance (or who aren't a group
  // admin) get the adoption landing page instead of the dashboard.
  if (!isAdmin) {
    return <ComplianceLandingPage />;
  }

  const onToggleArchive = async (row: FindingRow) => {
    try {
      await updateFinding({ id: row.id, is_archived: !row.is_archived }).unwrap();
      message.success(row.is_archived ? "Restored from archive" : "Archived");
    } catch {
      message.error("Could not update finding");
    }
  };

  const columns: ColumnsType<FindingRow> = [
    {
      title: "Severity",
      dataIndex: "severity",
      key: "severity",
      width: 130,
      sorter: (a, b) => SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity),
      defaultSortOrder: "ascend",
      render: (s: Severity) => <SeverityTag severity={s} />,
    },
    {
      title: "Rule",
      dataIndex: "rule_name",
      key: "rule_name",
      render: (v: string) => (
        <Typography.Text strong style={{ fontSize: 13 }}>
          {v}
        </Typography.Text>
      ),
    },
    {
      title: "Clinician",
      dataIndex: "clinician_name",
      key: "clinician_name",
      width: 160,
      sorter: (a, b) => a.clinician_name.localeCompare(b.clinician_name),
      render: (v: string) => <Typography.Text style={{ fontSize: 13 }}>{v}</Typography.Text>,
    },
    {
      title: "Age",
      dataIndex: "created_at",
      key: "age",
      width: 90,
      sorter: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      render: (v: string, row) => {
        const age = ageDays(v);
        const breachedSla = age > SLA_DAYS[row.severity] && !CLOSED_STATUSES.includes(row.status);
        return (
          <Flex align="center" gap={6}>
            <Typography.Text
              style={{
                fontSize: 13,
                color: breachedSla ? TONE.danger.fg : undefined,
                fontWeight: breachedSla ? 600 : 400,
              }}
            >
              {ageLabel(age)}
            </Typography.Text>
            {breachedSla ? (
              <Tooltip title={`SLA breached — ${SLA_DAYS[row.severity]}-day target for ${row.severity}`}>
                <WarningOutlined style={{ color: TONE.danger.accent, fontSize: 12 }} />
              </Tooltip>
            ) : null}
          </Flex>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 140,
      filters: (Object.keys(STATUS_META) as Status[]).map((s) => ({ text: STATUS_META[s].label, value: s })),
      onFilter: (val, row) => row.status === val,
      render: (s: Status) => <StatusTag status={s} />,
    },
    {
      title: "Assignee",
      dataIndex: "assignee_name",
      key: "assignee_name",
      width: 160,
      render: (v: string | null) =>
        v ? <Typography.Text style={{ fontSize: 13 }}>{v}</Typography.Text> : <Tag>Unassigned</Tag>,
    },
    {
      title: "",
      key: "archive",
      width: 120,
      render: (_v, row) => (
        <Button
          size="small"
          icon={row.is_archived ? <UndoOutlined /> : <InboxOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            onToggleArchive(row);
          }}
        >
          {row.is_archived ? "Unarchive" : "Archive"}
        </Button>
      ),
    },
  ] as ColumnType<FindingRow>[];

  if (isLoading) {
    return (
      <div className={styles.compliance__container}>
        <ComplianceDashboardHeader />
        <ComplianceTabs />
        <Skeleton active paragraph={{ rows: 10 }} />
      </div>
    );
  }

  if (!data?.has_active_rules) {
    return (
      <div className={styles.compliance__container}>
        <ComplianceDashboardHeader />
        <ComplianceTabs />
        <ComplianceEmptyState
          icon={<SafetyOutlined />}
          title="No compliance rules configured yet"
          description="Once you add rules, findings will appear here as the AI evaluates each note your clinicians generate. Set rules per template — what every note must document, from risk language to required elements — and Nudge handles the rest."
        />
      </div>
    );
  }

  return (
    <div className={styles.compliance__container}>
      <ComplianceDashboardHeader />

      <ComplianceTabs />

      {stats.criticalOpen > 0 || stats.slaBreached > 0 ? (
        <ComplianceBanner
          tone="danger"
          icon={<FireOutlined />}
          style={{ marginBottom: 16 }}
          action={
            <Button
              type="link"
              size="small"
              onClick={() => setQuickPreset("critical")}
              style={{ color: TONE.danger.fg, fontWeight: 600, padding: 0 }}
            >
              Show critical →
            </Button>
          }
        >
          <strong>{stats.criticalOpen} critical</strong> findings open
          {stats.slaBreached > 0 ? (
            <>
              {" "}
              · <strong>{stats.slaBreached}</strong> have breached their SLA target
            </>
          ) : null}
          . Triage now.
        </ComplianceBanner>
      ) : null}

      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <StatTile
            label="Open"
            value={stats.open}
            sublabel={`${stats.inReview} in review`}
            onClick={() => {
              setQuickPreset("all");
              setStatusFilter(["open"]);
            }}
          />
        </Col>
        <Col xs={12} md={6}>
          <StatTile
            label="Critical open"
            value={stats.criticalOpen}
            sublabel="Needs immediate triage"
            emphasis="critical"
            onClick={() => setQuickPreset("critical")}
            active={quickPreset === "critical"}
          />
        </Col>
        <Col xs={12} md={6}>
          <StatTile
            label="SLA breached"
            value={stats.slaBreached}
            sublabel="Past target resolution"
            emphasis="critical"
            onClick={() => setQuickPreset("sla")}
            active={quickPreset === "sla"}
          />
        </Col>
        <Col xs={12} md={6}>
          <StatTile
            label="Unassigned"
            value={stats.unassigned}
            sublabel="No owner yet"
            onClick={() => setQuickPreset("unassigned")}
            active={quickPreset === "unassigned"}
          />
        </Col>
      </Row>

      {/* Preset chips */}
      <Flex gap={6} wrap="wrap" style={{ marginBottom: 16 }}>
        {(
          [
            { key: "all", label: "All" },
            { key: "my", label: "Assigned to me" },
            { key: "critical", label: "Critical only" },
            { key: "unassigned", label: "Unassigned" },
            { key: "sla", label: "SLA breached" },
            { key: "archived", label: `Archived${stats.archivedCount ? ` (${stats.archivedCount})` : ""}` },
          ] as { key: typeof quickPreset; label: string }[]
        ).map((p) => (
          <Button
            key={p.key}
            size="small"
            type={quickPreset === p.key ? "primary" : "default"}
            onClick={() => setQuickPreset(p.key)}
          >
            {p.label}
          </Button>
        ))}
      </Flex>

      {/* Filter bar */}
      <Flex gap={8} wrap="wrap" align="center" style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Search rule, clinician, client, rationale…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
          className={styles.compliance__filterBar_search}
        />
        <Select<Status[]>
          mode="multiple"
          placeholder="Status"
          value={statusFilter}
          onChange={setStatusFilter}
          className={styles.compliance__filterBar_select}
          maxTagCount="responsive"
          suffixIcon={<FilterOutlined />}
          options={(Object.keys(STATUS_META) as Status[]).map((s) => ({ value: s, label: STATUS_META[s].label }))}
        />
        <Select<Severity[]>
          mode="multiple"
          placeholder="Severity"
          value={severityFilter}
          onChange={setSeverityFilter}
          className={styles.compliance__filterBar_select}
          maxTagCount="responsive"
          suffixIcon={<FilterOutlined />}
          options={SEVERITY_ORDER.map((s) => ({ value: s, label: SEVERITY_META[s].label }))}
        />
        <Select<number | null>
          placeholder="Assignee"
          value={assigneeFilter}
          onChange={(v) => setAssigneeFilter(v ?? null)}
          allowClear
          className={styles.compliance__filterBar_select}
          suffixIcon={<FilterOutlined />}
          options={assignees.map((a) => ({ value: a.id, label: a.name }))}
        />
        <Typography.Text className={styles.compliance__filterBar_count}>
          {filtered.length} of {findings.length} findings
        </Typography.Text>
      </Flex>

      <Table<FindingRow>
        columns={columns}
        dataSource={filtered}
        rowKey="id"
        size="middle"
        scroll={{ x: 900 }}
        pagination={{ pageSize: 20, showSizeChanger: false, hideOnSinglePage: false }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Flex vertical align="center" gap={4}>
                  <Typography.Text>No findings match these filters.</Typography.Text>
                  <Typography.Text style={{ color: "#9ca3af", fontSize: 12 }}>
                    Try clearing filters or selecting a different preset.
                  </Typography.Text>
                </Flex>
              }
            />
          ),
        }}
        onRow={(record) => ({
          onClick: () => navigate(`/compliance/findings/${record.id}`),
          style: { cursor: "pointer" },
        })}
      />
    </div>
  );
};

export default FindingsInbox;
